import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  type Certificate,
  RequestError,
  type ReviewDetail,
  type ReviewOutcome,
  type ReviewQueueRow,
  type ReviewStatement,
  api,
} from '../../lib/api'
import { faDate, faNum } from '../platform/labels'

const QUEUE_STATUS: Record<ReviewQueueRow['status'], { fa: string; badge: string }> = {
  submitted: { fa: 'در انتظار بازبینی', badge: 'badge--warn' },
  in_review: { fa: 'در حال بازبینی', badge: 'badge--stored' },
  finalized: { fa: 'نهایی شده', badge: 'badge--good' },
  returned: { fa: 'برگشت داده شده', badge: 'badge--bad' },
  not_submitted: { fa: 'ارسال نشده', badge: '' },
}

const BASIS_FA: Record<string, string> = {
  legacy_quiz: 'خودارزیابی پیشین',
  inferred: 'استنتاجی',
  self_report: 'اظهار مؤسسه',
  explained: 'همراه با توضیح',
  document_verified: 'مستند',
  assessor_verified: 'تأیید ارزیاب',
}

/**
 * «لیست موسسات در صف ارزیابی». Open reviews first, in the order they arrived;
 * charities that have not submitted are listed with how much of their picture
 * is known, because «not submitted at 40%» is useful to know too.
 */
export function ReviewQueuePage() {
  const queue = useQuery({ queryKey: ['review-queue'], queryFn: api.reviewQueue })
  return (
    <div className="container stack">
      <div className="card">
        <h1>مؤسسه‌ها در صف ارزیابی</h1>
        <p className="meta">ارزیابی‌های فرستاده‌شده را بازبینی و نهایی کنید. هر بازبینی را یک ارزیاب برمی‌دارد.</p>
      </div>
      {queue.isLoading || !queue.data ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>مؤسسه</th>
                  <th>وضعیت</th>
                  <th>فرستاده شده</th>
                  <th>روشن‌شده</th>
                  <th>ارزیاب</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {queue.data.queue.map((row) => (
                  <tr key={row.orgId}>
                    <td>{row.orgName}</td>
                    <td>
                      <span className={`badge ${QUEUE_STATUS[row.status].badge}`}>{QUEUE_STATUS[row.status].fa}</span>
                    </td>
                    <td>{row.submittedOn ? faDate(row.submittedOn) : '—'}</td>
                    <td>
                      <span className="num">
                        {faNum(Math.round((row.known / Math.max(row.total, 1)) * 100))}٪
                      </span>
                    </td>
                    <td className="ltr">{row.reviewerEmail ?? '—'}</td>
                    <td>
                      {row.reviewId !== null && row.status !== 'not_submitted' && (
                        <Link
                          to={`/review/${row.reviewId}`}
                          className={`btn btn--sm ${row.status === 'submitted' || row.status === 'in_review' ? '' : 'btn--quiet'}`}
                        >
                          {row.status === 'submitted' || row.status === 'in_review' ? 'بازبینی' : 'مشاهده'}
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

type Filter = 'known' | 'evidence' | 'key' | 'scored' | 'all'

const FILTER_FA: Record<Filter, string> = {
  known: 'روشن‌شده‌ها',
  evidence: 'دارای سند',
  key: 'کلیدی',
  scored: 'امتیازداده‌شده',
  all: 'همه',
}

/**
 * «بازبینی ارزیابی». Statement by statement: what the organization's picture
 * says and on what basis, the evidence the platform holds, and the assessor's
 * final score. The badge decision at the bottom shows what §6-1 computes with
 * these scores, so a departure from it is made knowingly.
 */
export function ReviewPage() {
  const { id } = useParams()
  const reviewId = Number(id)
  const queryClient = useQueryClient()
  const detail = useQuery({ queryKey: ['review', reviewId], queryFn: () => api.reviewDetail(reviewId) })
  const [filter, setFilter] = useState<Filter>('known')
  const [dimension, setDimension] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const set = (d: ReviewDetail) => {
    setError(null)
    queryClient.setQueryData(['review', reviewId], d)
    void queryClient.invalidateQueries({ queryKey: ['review-queue'] })
  }
  const fail = (err: unknown) => setError(err instanceof RequestError ? err.messageFa : 'انجام نشد.')
  const start = useMutation({ mutationFn: () => api.startReview(reviewId), onSuccess: set, onError: fail })

  const data = detail.data
  const statements = useMemo(() => {
    if (!data) return []
    return data.statements.filter((s) => {
      if (dimension && s.dimensionCode !== dimension) return false
      switch (filter) {
        case 'known':
          return s.selfScore !== null || s.evidence.length > 0 || s.finalScore !== null
        case 'evidence':
          return s.evidence.length > 0
        case 'key':
          return s.isKey
        case 'scored':
          return s.finalScore !== null
        default:
          return true
      }
    })
  }, [data, filter, dimension])

  if (detail.isLoading || !data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  const { review } = data
  const editable = review.status === 'in_review' && review.mine
  const scored = data.statements.filter((s) => s.finalScore !== null).length
  const dimensions = [...new Map(data.statements.map((s) => [s.dimensionCode, s.dimensionTitleFa])).entries()]

  return (
    <div className="container container--wide stack">
      <div className="card">
        <Link to="/review" className="btn btn--quiet btn--sm">
          بازگشت به صف
        </Link>
        <h1>بازبینی ارزیابی: {review.orgName}</h1>
        <p className="meta">
          فرستاده شده در {faDate(review.submittedOn)}
          {review.reviewerEmail && (
            <>
              {' · '}ارزیاب: <span className="ltr">{review.reviewerEmail}</span>
            </>
          )}
          {' · '}
          <span className="num">{faNum(scored)}</span> مورد امتیاز گرفته
        </p>
        {review.submitNoteFa && <div className="notice notice--info">یادداشت مؤسسه: {review.submitNoteFa}</div>}
        {error && <p className="notice notice--error">{error}</p>}
        {review.status === 'submitted' && (
          <button type="button" className="btn btn--sm" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'در حال برداشتن…' : 'برداشتن این بازبینی'}
          </button>
        )}
        {review.status === 'in_review' && !review.mine && (
          <p className="notice notice--info">این بازبینی را ارزیاب دیگری برداشته است. فقط می‌توانید ببینید.</p>
        )}
        {review.status === 'finalized' && (
          <div className="notice notice--info">
            نهایی شده در {faDate(review.closedOn)} · {review.outcome ? OUTCOME_FA[review.outcome] : ''}
            {review.outcomeNoteFa && <div>{review.outcomeNoteFa}</div>}
          </div>
        )}
      </div>

      <div className="card">
        <h2>نشان، پیش و پس از این بازبینی</h2>
        <CertificateCompare before={data.current} after={data.withReview} />
      </div>

      <div className="card">
        <div className="tabs" role="tablist" aria-label="کدام موارد">
          {(Object.keys(FILTER_FA) as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              className={`tab ${filter === f ? 'is-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {FILTER_FA[f]}
            </button>
          ))}
        </div>
        <div className="row" style={{ flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            className={`btn btn--sm ${dimension === null ? '' : 'btn--quiet'}`}
            onClick={() => setDimension(null)}
          >
            همه بخش‌ها
          </button>
          {dimensions.map(([code, title]) => (
            <button
              key={code}
              type="button"
              className={`btn btn--sm ${dimension === code ? '' : 'btn--quiet'}`}
              onClick={() => setDimension(code)}
            >
              {title}
            </button>
          ))}
        </div>
        <p className="meta">
          <span className="num">{faNum(statements.length)}</span> مورد در این فهرست
        </p>
        {statements.map((s) => (
          <StatementRow key={`${s.code}-${s.levelNo}`} reviewId={reviewId} s={s} editable={editable} onSaved={set} onError={fail} />
        ))}
      </div>

      {editable && <Decision reviewId={reviewId} data={data} onDone={set} onError={fail} />}
    </div>
  )
}

const OUTCOME_FA: Record<ReviewOutcome, string> = {
  awarded: 'نشان اعطا شد',
  conditional: 'نشان مشروط',
  none: 'بدون نشان',
}

const VERDICT_FA: Record<string, string> = { awarded: 'اخذ شده', conditional: 'مشروط', not_yet: 'هنوز نه' }

function CertificateCompare({ before, after }: { before: Certificate; after: Certificate }) {
  return (
    <div className="table-scroll">
      <table className="table table--compact">
        <thead>
          <tr>
            <th>سطح</th>
            <th>امتیاز فعلی</th>
            <th>با امتیازهای این بازبینی</th>
            <th>آنچه هنوز مانع است</th>
          </tr>
        </thead>
        <tbody>
          {after.levels.map((l) => {
            const b = before.levels.find((x) => x.levelNo === l.levelNo)
            return (
              <tr key={l.levelNo}>
                <td>
                  {l.tier} · سطح <span className="num">{faNum(l.levelNo)}</span>
                </td>
                <td>
                  <span className="num">{faNum(b?.percent ?? 0)}٪</span> · {VERDICT_FA[b?.verdict ?? 'not_yet']}
                </td>
                <td>
                  <span className="num">{faNum(l.percent)}٪</span> · <strong>{VERDICT_FA[l.verdict]}</strong>
                </td>
                <td className="meta">{l.blockingFa.slice(0, 2).join(' ') || '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {!after.authoritative && (
        <p className="meta">
          این محاسبه رسمی نیست: فهرست گزاره‌های کلیدی در استاندارد تعریف نشده و آنچه اینجا کلیدی شمرده شده یک کف است.
          تصمیم نهایی با شماست.
        </p>
      )}
    </div>
  )
}

function StatementRow({
  reviewId,
  s,
  editable,
  onSaved,
  onError,
}: {
  reviewId: number
  s: ReviewStatement
  editable: boolean
  onSaved: (d: ReviewDetail) => void
  onError: (err: unknown) => void
}) {
  const [note, setNote] = useState(s.noteFa ?? '')
  const save = useMutation({
    mutationFn: (finalScore: number | null) =>
      api.saveReviewScore(reviewId, { statementCode: s.code, levelNo: s.levelNo, finalScore, noteFa: note.trim() || null }),
    onSuccess: onSaved,
    onError,
  })
  const changed = s.finalScore !== null && s.selfScore !== s.finalScore

  return (
    <article className={`review-row ${s.finalScore !== null ? 'is-scored' : ''}`}>
      <div className="meta">
        <span className="num ltr">{s.code}</span> · سطح <span className="num">{faNum(s.levelNo)}</span> · {s.dimensionTitleFa}
        {s.isKey && <span className="badge badge--bad">کلیدی</span>}
      </div>
      <div>{s.textFa}</div>
      {s.scoringFa && <div className="task__done">{s.scoringFa}</div>}

      <div className="review-row__grid">
        <div>
          <div className="label">خودارزیابی مؤسسه</div>
          {s.selfScore === null ? (
            <span className="muted">روشن نشده</span>
          ) : (
            <>
              <span className="num">{faNum(s.selfScore)}</span> از <span className="num">۳</span> ·{' '}
              {BASIS_FA[s.selfBasis ?? ''] ?? s.selfBasis}
              {s.rationaleFa && <div className="task__done">{s.rationaleFa.slice(0, 280)}</div>}
            </>
          )}
        </div>
        <div>
          <div className="label">شواهد</div>
          {s.evidence.length === 0 ? (
            <span className="muted">سندی نیست</span>
          ) : (
            s.evidence.map((e) => (
              <div key={`${e.documentId}-${e.pageNo}`} className="task__done">
                <span className={`badge ${e.verdict === 'supports' ? 'badge--good' : 'badge--bad'}`}>
                  {e.verdict === 'supports' ? 'تأیید می‌کند' : 'نقض می‌کند'}
                </span>{' '}
                «{e.filename}»{e.pageNo !== null && <> ص <span className="num">{faNum(e.pageNo)}</span></>}
                {e.quoteFa && <div>«{e.quoteFa.slice(0, 240)}»</div>}
              </div>
            ))
          )}
        </div>
        <div>
          <div className="label">امتیاز نهایی ارزیاب</div>
          <div className="score-pick" role="group" aria-label={`امتیاز نهایی ${s.code}`}>
            {[0, 1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={`score-pick__btn num ${s.finalScore === n ? 'is-selected' : ''}`}
                aria-pressed={s.finalScore === n}
                disabled={!editable || save.isPending}
                onClick={() => save.mutate(s.finalScore === n ? null : n)}
              >
                {faNum(n)}
              </button>
            ))}
          </div>
          {changed && <span className="badge badge--warn">با خودارزیابی فرق دارد</span>}
          {editable ? (
            <input
              aria-label="یادداشت ارزیاب"
              placeholder="یادداشت (اختیاری)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => {
                if (s.finalScore !== null && note.trim() !== (s.noteFa ?? '')) save.mutate(s.finalScore)
              }}
            />
          ) : (
            s.noteFa && <div className="task__done">{s.noteFa}</div>
          )}
        </div>
      </div>
    </article>
  )
}

function Decision({
  reviewId,
  data,
  onDone,
  onError,
}: {
  reviewId: number
  data: ReviewDetail
  onDone: (d: ReviewDetail) => void
  onError: (err: unknown) => void
}) {
  const after = data.withReview
  const suggested: { outcome: ReviewOutcome; level: number | null } =
    after.awardedLevel !== null && (after.conditionalLevel === null || after.conditionalLevel <= after.awardedLevel)
      ? { outcome: 'awarded', level: after.awardedLevel }
      : after.conditionalLevel !== null
        ? { outcome: 'conditional', level: after.conditionalLevel }
        : { outcome: 'none', level: null }
  const [outcome, setOutcome] = useState<ReviewOutcome>(suggested.outcome)
  const [level, setLevel] = useState<number | null>(suggested.level ?? 1)
  const [note, setNote] = useState('')
  const [returnNote, setReturnNote] = useState('')
  const departs = outcome !== suggested.outcome || (outcome !== 'none' && level !== suggested.level)

  const finalize = useMutation({
    mutationFn: () => api.finalizeReview(reviewId, { outcome, levelNo: outcome === 'none' ? null : level, noteFa: note.trim() || null }),
    onSuccess: onDone,
    onError,
  })
  const sendBack = useMutation({ mutationFn: () => api.returnReview(reviewId, returnNote.trim()), onSuccess: onDone, onError })

  const tierOf = (n: number | null) => after.levels.find((l) => l.levelNo === n)?.tier ?? ''

  return (
    <div className="grid-2 grid-2--cards">
      <div className="card">
        <h2>ثبت نتیجه نهایی و تعیین نشان</h2>
        <p className="meta">
          قواعد بند ۶-۱ با امتیازهای شما پیشنهاد می‌دهد:{' '}
          <strong>
            {suggested.outcome === 'none' ? 'بدون نشان' : `${OUTCOME_FA[suggested.outcome]} · ${tierOf(suggested.level)}`}
          </strong>
          . با نهایی کردن، امتیازهای شما با پایهٔ «تأیید ارزیاب» در تصویر مؤسسه می‌نشینند.
        </p>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="outcome">تصمیم</label>
            <select id="outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as ReviewOutcome)}>
              <option value="awarded">{OUTCOME_FA.awarded}</option>
              <option value="conditional">{OUTCOME_FA.conditional}</option>
              <option value="none">{OUTCOME_FA.none}</option>
            </select>
          </div>
          {outcome !== 'none' && (
            <div className="field">
              <label htmlFor="level">سطح نشان</label>
              <select id="level" value={level ?? 1} onChange={(e) => setLevel(Number(e.target.value))}>
                {after.levels.map((l) => (
                  <option key={l.levelNo} value={l.levelNo}>
                    {l.tier} · سطح {faNum(l.levelNo)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="decision-note">{departs ? 'دلیل تصمیم (لازم است، چون با پیشنهاد قواعد فرق دارد)' : 'یادداشت برای مؤسسه'}</label>
          <textarea id="decision-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn"
          disabled={finalize.isPending || (departs && note.trim().length < 10)}
          onClick={() => finalize.mutate()}
        >
          {finalize.isPending ? 'در حال ثبت…' : 'ثبت نتیجه نهایی'}
        </button>
      </div>

      <div className="card">
        <h2>برگرداندن به مؤسسه</h2>
        <p className="meta">اگر شواهد کافی نیست، بازبینی را با یادداشت برگردانید. چیزی در تصویر مؤسسه عوض نمی‌شود.</p>
        <div className="field">
          <label htmlFor="return-note">چه چیزی لازم است</label>
          <textarea id="return-note" value={returnNote} onChange={(e) => setReturnNote(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={sendBack.isPending || returnNote.trim().length < 10}
          onClick={() => sendBack.mutate()}
        >
          برگرداندن
        </button>
      </div>
    </div>
  )
}
